import { Paging } from '@zdrbm/zdr-interfaces';
import { IAbortController } from '../../../infra/httpClient/AbortController';
import { IAutocompleteSuggestions } from '../model/AutocompleteSuggestions';
import { IConversation } from '../model/Conversation';
import { IPlacerAssistantFactory } from '../model/PlacerAssistantFactory';
import { IPlacerAssistantClient } from './PlacerAssistantClient';
import { IConversationRun } from '../model/ConversationRun';
import { ScreenshotInfoDTO } from './AssistantDTOs';
import { IImage } from '../../../infra';
import { PiError } from 'features/pi/model/pi-errors/PiError';

export const DEFAULT_IMAGE_QUALITY = 0.8;
export const DEFAULT_VALID_IMAGE_SIZE = 100;

export interface IPlacerAssistantService {
    getRunById(conversationId: string, runId: string): Promise<IConversationRun>;
    getConversationById(conversationId: string): Promise<IConversation | undefined>;
    listConversations(paging: Paging): Promise<IConversation[]>;
    getSuggestions(
        query: string,
        session?: string,
        abortController?: IAbortController,
    ): Promise<IAutocompleteSuggestions>;
    deleteConversation(conversationId: string): Promise<boolean>;
    createConversation(): Promise<IConversation>;
    sendMessage(
        conversationId: string,
        content: string,
        image: IImage | undefined,
    ): Promise<IConversationRun>;
    sendPositiveRunFeedback(conversationId: string, runId: string, content: string): Promise<void>;
    sendNegativeRunFeedback(conversationId: string, runId: string, content: string): Promise<void>;

    generateConversationTitle(conversationId: string): Promise<IConversation>;
}

export interface PlacerAssistantServiceOptions {
    supportsScreenshot: boolean;
    validImageSize?: number;
}

export class PlacerAssistantService implements IPlacerAssistantService {
    constructor(
        private client: IPlacerAssistantClient,
        private factory: IPlacerAssistantFactory,
        private options: PlacerAssistantServiceOptions,
    ) {}

    sendPositiveRunFeedback(conversationId: string, runId: string, content: string): Promise<void> {
        return this.client.sendRunFeedback({
            conversationId,
            runId,
            score: 5,
            feedback: content,
        });
    }
    sendNegativeRunFeedback(conversationId: string, runId: string, content: string): Promise<void> {
        return this.client.sendRunFeedback({
            conversationId,
            runId,
            score: 1,
            feedback: content,
        });
    }

    async generateConversationTitle(conversationId: string): Promise<IConversation> {
        const result = await this.client.generateConversationTitle({
            conversationId,
        });
        const { conversation } = result;

        return this.factory.createConversation(
            {
                id: conversation.id,
                title: conversation.metadata.title,
                timestamp: conversation.timestamp,
            },
            [],
        );
    }

    private getValidImageSize(): number {
        return this.options.validImageSize || DEFAULT_VALID_IMAGE_SIZE;
    }

    private extractBase64FromDataUri(dataUri: string): string {
        const base64Index = dataUri.indexOf(',') + 1;
        return dataUri.substring(base64Index);
    }

    async getRunById(conversationId: string, runId: string): Promise<IConversationRun> {
        const { run: runDTO } = await this.client.getRun({ conversationId, runId });
        const run = this.factory.createConversationRun(runDTO);
        return run;
    }

    private getDataUriSizeInKilobytes(dataUri: string): number {
        if (!dataUri) return 0;

        // Extract the base64 part (after the comma)
        const base64String = dataUri.split(',')[1];
        if (!base64String) return 0;

        // Calculate size: base64 uses 4 characters to represent 3 bytes
        // So the decoded size is (n * 3) / 4 where n is the length without padding
        const paddingCount = base64String.endsWith('==') ? 2 : base64String.endsWith('=') ? 1 : 0;
        const sizeInBytes = (base64String.length * 3) / 4 - paddingCount;

        // Convert bytes to kilobytes
        return sizeInBytes / 1024;
    }

    isImageSizeValid(dataUri: string): boolean {
        const sizeInKilobytes = this.getDataUriSizeInKilobytes(dataUri);
        return sizeInKilobytes <= this.getValidImageSize();
    }

    private async getValidImageForMessage(image: IImage): Promise<string> {
        let dataUri = await image.getJPegDataUri(DEFAULT_IMAGE_QUALITY);

        if (!this.isImageSizeValid(dataUri)) {
            const reducedImage = await image.fit(1024, 1024);
            dataUri = await reducedImage.getJPegDataUri(DEFAULT_IMAGE_QUALITY);

            if (!this.isImageSizeValid(dataUri)) {
                const dataUri = await reducedImage.getJPegDataUri(0.6);
                if (!this.isImageSizeValid(dataUri)) {
                    throw new PiError('Image size is still too large after resizing');
                }
            }
        }

        return dataUri;
    }

    async getConversationById(conversationId: string): Promise<IConversation | undefined> {
        const [{ conversation }, { runs }] = await Promise.all([
            this.client.getConversation({ conversationId: conversationId }),
            this.client.listConversationRuns({ conversationId: conversationId }),
        ]);
        return conversation ? this.factory.createConversation(conversation, runs) : undefined;
    }

    async deleteConversation(conversationId: string): Promise<boolean> {
        try {
            await this.client.deleteConversation({ conversationId });
            return true;
        } catch (error) {
            return false;
        }
    }

    async listConversations(paging: Paging): Promise<IConversation[]> {
        const result = await this.client.listConversations({ paging });
        return result.conversations.map((conversation) => {
            return this.factory.createConversation(
                {
                    id: conversation.id,
                    title: conversation.metadata.title,
                    timestamp: conversation.timestamp,
                },
                [],
            );
        });
    }

    async sendMessage(
        conversationId: string,
        content: string,
        image: IImage | undefined,
    ): Promise<IConversationRun> {
        let screenshotInfo: ScreenshotInfoDTO | undefined = {
            screenshot_support: this.options.supportsScreenshot,
            screenshot_permission_denied: false,
        };
        if (image) {
            const dataUri = await this.getValidImageForMessage(image);
            screenshotInfo.screenshot_base64 = this.extractBase64FromDataUri(dataUri);
        }

        const { run } = await this.client.createRun({ conversationId, content, screenshotInfo });

        const runModel = this.factory.createConversationRun(run);
        return runModel;
    }

    async createConversation(): Promise<IConversation> {
        const result = await this.client.createConversation();
        const conversation = this.factory.createNewConversation(result.conversation_id);
        return conversation;
    }

    async getSuggestions(
        query: string,
        session?: string,
        abortController?: IAbortController,
    ): Promise<IAutocompleteSuggestions> {
        const { suggestions, session: receivedSession } = await this.client.getSuggestions(
            { query, session },
            { abortController },
        );
        const autocompleteSuggestions = this.factory.createAutocompleteSuggestions(
            query,
            suggestions,
            receivedSession,
        );

        return autocompleteSuggestions;
    }
}
